import { useEffect, useState } from "react";
import { Badge, Menu } from "antd";
import type { MenuProps } from "antd";
import { NavLink } from "react-router-dom";
import { hasRoleAccess } from "../../utils/constants";
import { useUser } from "../../context/UserContext";
import { useNotifications } from "../../context/NotificationsProvider";
import {
  DEFAULT_GENERAL_CONFIG_ORDER,
  GENERAL_CONFIG_SECTIONS,
  GeneralConfigSection,
  GeneralConfigSectionKey,
} from "../../utils/generalConfigSections";
import {
  MENU_ORDER_UPDATED_EVENT,
  getMyMenuOrder,
} from "../../utils/menuOrderService";
import {
  DashboardOutlined,
  ScheduleOutlined,
  SettingOutlined,
  AuditOutlined,
} from "@ant-design/icons";

type SidebarProps = {
  collapsed?: boolean;
  onNavigate?: () => void;
};

const sectionByKey = new Map(
  GENERAL_CONFIG_SECTIONS.map((section) => [section.key, section]),
);

const buildGeneralConfigChildren = (
  order: GeneralConfigSectionKey[],
): MenuProps["items"] => {
  const orderedKeys = [
    ...order.filter((key) => sectionByKey.has(key)),
    ...DEFAULT_GENERAL_CONFIG_ORDER.filter((key) => !order.includes(key)),
  ];

  return orderedKeys
    .map((key) => sectionByKey.get(key))
    .filter((section): section is GeneralConfigSection => Boolean(section))
    .map((section) => ({
      key: section.key,
      label: <NavLink to={section.path}>{section.label}</NavLink>,
    }));
};

const Sidebar = ({ collapsed, onNavigate }: SidebarProps) => {
  const { roles } = useUser();
  const { documentsToUploadCount } = useNotifications();
  const canSeeGeneralConfig = hasRoleAccess(roles, ["ADMIN", "HR"]);
  const [generalConfigOrder, setGeneralConfigOrder] = useState<
    GeneralConfigSectionKey[]
  >(DEFAULT_GENERAL_CONFIG_ORDER);

  useEffect(() => {
    if (!canSeeGeneralConfig) return;

    let isMounted = true;
    getMyMenuOrder()
      .then((order) => {
        if (isMounted && order.length) {
          setGeneralConfigOrder(order);
        }
      })
      .catch((error) => {
        console.error("Failed to load menu order:", error);
      });

    const onMenuOrderUpdated = (event: Event) => {
      const { detail } = event as CustomEvent<GeneralConfigSectionKey[]>;
      if (detail?.length) {
        setGeneralConfigOrder(detail);
      }
    };
    window.addEventListener(MENU_ORDER_UPDATED_EVENT, onMenuOrderUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener(MENU_ORDER_UPDATED_EVENT, onMenuOrderUpdated);
    };
  }, [canSeeGeneralConfig]);

  const items: MenuProps["items"] = [
    hasRoleAccess(roles, ["ADMIN", "MANAGER"]) && {
      key: "dashboard",
      label: <NavLink to="/">Management Dashboard</NavLink>,
      icon: <DashboardOutlined />,
    },
    {
      key: "my-leave-requests",
      label: (
        <NavLink
          to="/my-leave-requests"
          className="flex items-center justify-between gap-2"
        >
          My Requests
          {documentsToUploadCount > 0 && (
            <Badge count={documentsToUploadCount} size="small" />
          )}
        </NavLink>
      ),
      icon: <AuditOutlined />,
    },
    hasRoleAccess(roles, ["ADMIN", "ADMINISTRATIV"]) && {
      key: "pending-administrativ",
      label: (
        <NavLink to="/pending-administrativ">Pending Administrativ</NavLink>
      ),
      icon: <AuditOutlined />,
    },
    {
      key: "annual-leave",
      label: "Annual Leave",
      icon: <ScheduleOutlined />,

      children: [
        hasRoleAccess(roles, ["ADMIN", "MANAGER", "HR"])
          ? {
              key: "annual-team",
              label: <NavLink to="/annualLeave/team">Team Calendar</NavLink>,
            }
          : null,
        {
          key: "annual-calculator",
          label: (
            <NavLink to="/annualLeave/calculator">Leave Calculator</NavLink>
          ),
        },
      ].filter(Boolean) as MenuProps["items"],
    },
    canSeeGeneralConfig
      ? {
          key: "general-config",
          icon: <SettingOutlined />,
          label: (
            <NavLink to="/general-configuration">General Configuration</NavLink>
          ),
          children: buildGeneralConfigChildren(generalConfigOrder),
        }
      : null,
  ].filter(Boolean) as MenuProps["items"];

  return (
    <Menu
      mode="inline"
      items={items}
      className="app-sidebar-menu h-full border-0 pb-12"
      inlineCollapsed={collapsed}
      onClick={onNavigate}
    />
  );
};

export default Sidebar;
